import { logAppEvent, MIcons, MLink, MTextField, useFeatureFlag } from "@material-dev/insights-portal-ui-components/lib";
import MAlert from "@material-dev/insights-portal-ui-components/lib/ui-components/m-alert/MAlert";
import MAutocomplete from "@material-dev/insights-portal-ui-components/lib/ui-components/m-autocomplete";
import MButton from "@material-dev/insights-portal-ui-components/lib/ui-components/m-button/MButton";
import MDialog from "@material-dev/insights-portal-ui-components/lib/ui-components/m-dialog/MDialog";
import MForm, { MFormInstanceProps } from "@material-dev/insights-portal-ui-components/lib/ui-components/m-form/MForm";
import MInput from "@material-dev/insights-portal-ui-components/lib/ui-components/m-input/MInput";
import MSpinner from "@material-dev/insights-portal-ui-components/lib/ui-components/m-spinner/MSpinner";
import { FilterOptionsState } from "@mui/material";
import debounce from "lodash.debounce";
import React, { FC, memo, SyntheticEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { batch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { CreateAssignWorkdayProjectDatasetCommand, EntityIdDtoOfInteger, MergedProjectDto, ProjectSourceDto, WorkdayProjectType } from "app/ApiClient";
import { useAppDispatch, useAppSelector } from "app/store";
import { FIREBASE_EVENTS } from "const/firebase";
import { resetEncodingFilesState } from "slices/encodingFiles/encodingFilesSlice";
import {
  originProjectsSelector,
  resetOriginProjects,
  getOriginProjects,
} from "slices/originProjects/originProjectSlice";
import { NormalizedOriginProject } from "slices/originProjects/originProjectState";
import {
  checkDimensionProjectExists,
  createProject,
  projectSelector,
  resetProject,
  setAddProjectDialogOpenedState,
  setAddCompositeProjectDialogOpenedState,
  setSelectedOriginProject,
  setOpenCreateCompositeProject,
  setOpenCreateCustomDataProject,
  setOpenCreateWalrDataProject,
} from "slices/project/projectSlice";
import { createProjectFromEncoded } from "slices/projects/projectsSlice";
import { projectsDataSelector, resetProjectsData } from "slices/projectsData/projectsDataSlice";
import { projectSourcesSelector, getAllSources } from "slices/projectSources/projectSourcesSlice";
import { LoadingState } from "types";

import { useAddProjectOpened } from "../ProjectData/hooks/useAddProjectOpened";
import { originProjectFormData, ProjectFormData, projectViewModelSchema } from "./AddProjectFormModel";
import { useCreateProjectFormStyles } from "./AddProjectFormStyles";
import CreateCompositeProjectDialog from "components/CreateCompositeProjectDialog";
import BetaChip from "components/common/BetaChip/BetaChip";
import { getNormalizedOriginSourceLable } from "utils/projectSourceHelper";
import { DIMENSION_C } from "hooks/useIsCompositeProject";
import { CUSTOMDATA, WALRDATA } from "hooks";
import { CreateCustomDataProjectDialog } from "components/CreateCustomDataProjectDialog";
import { DATASETS_PATH } from "const/routes";
import { CreateWalrDataProjectDialog } from "components/CreateWalrDataProjectDialog/CreateWalrDataProjectDialog";
import { MAINTENANCE_TEAM_EMAIL } from "const/config";
import { getIn, useFormik } from "formik";
import { projectTypes } from "components/WorkDayProjects/AddWorkdayProjectDialog/CreateWorkDayProjectSchema";
import {
  createAndAssignWorkdayProjectDataset,  
  getWorkdayProjectsById,
  searchOriginWorkdayProjects, 
  resetWorkdayProject, 
} from "slices/WorkdayProjects/workdayProjectSlice";
import { WorkdayProjectsDto } from "app/SharedApiClient";

export type FormicSetFieldValueType = (
  field: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value: any,
  shouldValidate?: boolean | undefined,
) => void;

export const getExistingProjectMessages = (projectId: string) => (
  <div>No action has been made due to dataset ID {projectId} already existing in Data Manager. </div>
);

const DEBOUNCED_DELAY = 300;

const AddProjectDialog: FC = memo(() => {
  useAddProjectOpened();
  const formRef = useRef<MFormInstanceProps<ProjectFormData>>(null);
  const { classes: styles } = useCreateProjectFormStyles();
  const { originProjects, processing: isOriginProjectsLoading } = useSelector(originProjectsSelector);
  const {
    isAddProjectDialogOpened,
    isAddCompositeProjectDialogOpened,
    isOpenCreateCompositeProject,
    isOpenCreateCustomDataProject,
    isOpenCreateWalrDataProject,
    createProjectLoadingState,
    createdProject,
    existingProject,
    createProjectError,
  } = useSelector(projectSelector);
  const { processing: isLoadingDictionaryData } = useSelector(projectsDataSelector);
  const { projectSources } = useSelector(projectSourcesSelector);
  const [selectedProjectName, setSelectedProjectName] = useState<string | null>(null);
  const navigate = useNavigate();
  const [selectedProjectNumber, setSelectedProjectNumber] = useState<string | null>(null);
  const [selectedSource, setSelectedSource] = useState<ProjectSourceDto | null>(null);
  const { currentUser } = useAppSelector((state) => state.userState);
  const dispatch = useAppDispatch();
  const [searchTerm, setSearchTerm] = useState("");
  const { featureWalrDataSource } = useFeatureFlag();
  const {
    projectsBysearch: projects,
    loading,   
    currentProjectId,    
    workdayProjects: { allData }
  } = useAppSelector(
    (state) => state.workdayProject
  );
  const debounceSearch = useCallback(
    debounce((text: string) => {      
      dispatch(searchOriginWorkdayProjects({ search: text }));      
    }, DEBOUNCED_DELAY),
    [dispatch]
  ); 
  type WorkdayProjectOption = WorkdayProjectsDto & {
    label: string;
  }; 
  const formik = useFormik<ProjectFormData>({
    initialValues: {
      project: null,
      workdayProject: {
        name: "",
        workdayId: "",
        client: "",
        costCenter: "",
        workdayProjectType: undefined,
      }
    },
    validationSchema: projectViewModelSchema,
    onSubmit: (values) => {
      handleSubmit(values);
    },
  });  

  useEffect(() => {
    if (currentProjectId) {
      dispatch(getWorkdayProjectsById(currentProjectId));
    }
  }, [currentProjectId, dispatch]);

  useEffect(() => {
    if (searchTerm) {
      debounceSearch(searchTerm);
    }
    return () => {
      debounceSearch.cancel();
    };
  }, [searchTerm, debounceSearch]);
  
  useEffect(() => {
    if (createProjectLoadingState === LoadingState.succeed && createdProject?.id) {
      dispatch(resetProject());
      dispatch(setAddProjectDialogOpenedState(false));
      dispatch(resetWorkdayProject());
      navigate(`${DATASETS_PATH}/${createdProject.id ?? 0}/${createdProject.versionId ?? ""}`);
    }
  }, [createProjectLoadingState, dispatch, history, createdProject]);

  useEffect(() => {
    dispatch(getAllSources());
  }, [dispatch]);

  useEffect(() => {
    if (selectedProjectNumber) {
      const promise = dispatch(checkDimensionProjectExists(selectedProjectNumber));
      return () => {
        promise.abort?.();
      };
    }
  }, [selectedProjectNumber, dispatch]);

  const onProjectNumberChange = useCallback(
    (e: SyntheticEvent, value: NormalizedOriginProject | null) => {
      if (!value) return;
  
      formik.setFieldValue("project", value);
      setSelectedProjectNumber(value.name);
    },
    [formik]
  );

  useEffect(() => {
    const selectedProject = allData.find(
      (project) => project.id === currentProjectId
    );
  
    if (selectedProject) {
      formik.setValues({
        project: formik.values.project ?? null, // keep existing dataset selection
  
        workdayProject: {
          name: selectedProject.name ?? "",
          workdayId: selectedProject.workdayId ?? "",
          client: selectedProject.client ?? "",
          costCenter: selectedProject.costCenter ?? "",
          workdayProjectType:
            selectedProject.workdayProjectType === WorkdayProjectType.Tracker
              ? WorkdayProjectType.Tracker
              : selectedProject.workdayProjectType === WorkdayProjectType.Adhoc
              ? WorkdayProjectType.Adhoc
              : undefined,
        },
      });
  
      setSelectedProjectName(selectedProject.name ?? null);
    }
  }, [allData, currentProjectId, setSelectedProjectNumber, dispatch]);

  const onSourceSelected = useCallback(
    (source: ProjectSourceDto | null) => {
      if (source?.id) {
        setSelectedSource(source);
        if (source?.name === CUSTOMDATA) {
          openCreateCustomDataProject();
        }
        else if (source?.name === WALRDATA) {
          openCreateWalrDataProject();
        } else {
          dispatch(getOriginProjects(source.id));
        }
      }
    },
    [setSelectedSource],
  );

  const renderOptions = useCallback(
    (props: React.HTMLAttributes<HTMLLIElement>, option: NormalizedOriginProject) => {
      const adornmentValue = option.sourceCode ?? option.source;
      const adornment = adornmentValue ? `(${adornmentValue})` : "";
      return (
        <li
          {...props}
          key={`${option.name}-${option.label ?? ""}-${option.environmentKey ?? ""}-${option.sourceCode ?? ""}`}>
          {option.label ? `${option.name} ${adornment} - ${option.label}` : `${option.name} ${adornment}`}
        </li>
      );
    },
    [],
  );

  const getOptionLabel = useCallback((option: NormalizedOriginProject) => option.label ?? "", []);

  useEffect(() => {
    if (!isAddProjectDialogOpened) {
      setSelectedProjectNumber(null);
      setSelectedSource(null);
    }
  }, [isAddProjectDialogOpened]);

  const isAddEnabled = !!selectedSource &&
                        !!selectedProjectNumber &&
                        !!formik.values.workdayProject?.workdayId?.trim() &&
                        !!formik.values.workdayProject?.name?.trim() &&
                        !!formik.values.workdayProject?.workdayProjectType &&
                        !existingProject;

  const onCloseDialog = useCallback(() => {
    if (createProjectLoadingState === LoadingState.pending) {
      dispatch(setAddProjectDialogOpenedState(true));
    } else {
      formik.resetForm();
      setSelectedProjectName(null);
      setSelectedProjectNumber(null);
      setSelectedSource(null);
      dispatch(setAddProjectDialogOpenedState(false));
    }
  }, [createProjectLoadingState, dispatch, formik]);

  const handleInputChange = (_: SyntheticEvent<Element, Event>, newValue: string) => {
    setSearchTerm(newValue);
  };

  const onCloseCompositeDialog = useCallback(() => {
    dispatch(setAddCompositeProjectDialogOpenedState(false));
  }, [dispatch]);

  const handleSubmit = useCallback(
    async (values: ProjectFormData) => {   
      const { project, workdayProject } = values;     
      try {
        logAppEvent(FIREBASE_EVENTS.projectsPage.onLoadProjectClick, { projectId: project });        
        if (!existingProject && selectedSource && project) {
          // Batch the synchronous store updates
          batch(() => {
            dispatch(resetEncodingFilesState());
            dispatch(resetProjectsData());
            dispatch(setSelectedOriginProject(project));
          });

          // 1) Create the dataset
          const action = await dispatch(
            createProject({
              originProjectId: project.name,
              originProjectName: project.label,
              environmentKey: project.environmentKey,
              sourceId: selectedSource.id,
              sourceCode: project.sourceCode,
            })
          );
          let newDataset: EntityIdDtoOfInteger | undefined;
          if (createProject.fulfilled.match(action)) {
             newDataset = action.payload;
          }
          // Create & assign Workday mapping          
          const createAssignWorkdayCommand: CreateAssignWorkdayProjectDatasetCommand = {
            datasetId: newDataset?.id,
            datasetName: project.name,
            workdayId: workdayProject?.workdayId ?? "",
            name: workdayProject?.name ?? "",
            client: workdayProject?.client ?? "",
            costCenter: workdayProject?.costCenter ?? "",
            workdayProjectType: workdayProject?.workdayProjectType,
          };
          
          await dispatch(createAndAssignWorkdayProjectDataset(createAssignWorkdayCommand));

          // 3) Success UI path
          dispatch(resetProject());
          dispatch(resetWorkdayProject());
          onCloseDialog();          
        }  else {
          //  Redirected to existing project in table
          dispatch(resetProject());
          if (existingProject?.id) {
            navigate(`${DATASETS_PATH}/${existingProject.id ?? 0}/${existingProject.versionId ?? ""}`);
          } else if (existingProject?.originProjectId) {
            dispatch(
              createProjectFromEncoded({
                originProjectId: existingProject?.originProjectId,
              }),
            )
              .then(({ payload }) => {
                const res = payload as MergedProjectDto;
                navigate(`${DATASETS_PATH}/${res.id ?? 0}/${res.versionId ?? ""}`);
                return;
              })
              .catch((e) => {
                console.error(e);
                return;
              });
            onCloseDialog();
          }
        }
      } catch (err) {
        console.error(err);
      }
    },
    [dispatch, existingProject, onCloseDialog, navigate, selectedSource]
  );
  const filteredProjectSources = useMemo(() => {
    let data = projectSources?.filter((source) => source.name != DIMENSION_C);
    if (!featureWalrDataSource) {
      data = data?.filter((source) => source.name !== WALRDATA);
    }
    return data;
  }, [projectSources]);

  const sourceSelect = useMemo(
    () => (
      <div className={styles.sources}>
        {filteredProjectSources &&
          filteredProjectSources.map((source) => (
            <div className="button-container">
            <MButton
              name={source.name}
              title={getNormalizedOriginSourceLable(source.displayName)}
              type="button"
              variant="outlined"
              color="primary"
              onClick={() => onSourceSelected(source)}>
              {getNormalizedOriginSourceLable(source.displayName)}
            </MButton>
            </div>
          ))}
      </div>
    ),
    [styles, projectSources],
  );

  const getFilteredOptions = (
    options: NormalizedOriginProject[],
    state: FilterOptionsState<NormalizedOriginProject>,
  ) => {
    const normalizedInputValue = state.inputValue?.toLowerCase();
    if (normalizedInputValue) {
      return options.filter((opt) => state.getOptionLabel(opt)?.toLowerCase().includes(normalizedInputValue));
    }
    return options;
  };
  
  useEffect(() => {
    if (!isAddProjectDialogOpened) {
      formik.resetForm();
      setSelectedProjectName(null);
      setSelectedProjectNumber(null);
      setSelectedSource(null);
    }
  }, [isAddProjectDialogOpened, formik.resetForm]);

  const backToSources = useCallback(() => {
    formik.resetForm();
    setSelectedProjectName(null);
    setSelectedProjectNumber(null);
    setSelectedSource(null);
    dispatch(resetOriginProjects());
  }, [dispatch, formik]);
  
  const projectSelect = useMemo(
    () => (
      <>
        {existingProject && selectedProjectNumber && (
          <div className={styles.message}>
            <div className={styles.messageTitle}>Dataset already exists!</div>
            <div className={styles.messageBody}>{getExistingProjectMessages(selectedProjectNumber)}</div>
          </div>
        )}
        {createProjectError && (
          <MAlert severity="error" closable>
            Something wrong, Please contact an please contact an &nbsp;
            <MLink className={styles.errorLink} href={`mailto:${MAINTENANCE_TEAM_EMAIL}`}>
              Insights Portal administrator.
            </MLink>
             for more details.
          </MAlert>
        )}
       <MForm 
          formProps={{ 
            id: "project-form", 
            onSubmit: (event)=>{              
              handleSubmit(formik.values );
              event.preventDefault();
          } }}
          formRef={formRef}
          onSubmit={()=>{}}
          initialValues={originProjectFormData}
          validationSchema={projectViewModelSchema}>
          {() => (
            <>
              {!existingProject && (
                <div>
                  {isOriginProjectsLoading && <MSpinner isLoading />}
                  <div className={styles.headerlabel}>Enter your dataset ID</div>
                  <div className={styles.formItem}>                      
                  <MAutocomplete
                        options={originProjects}
                        value={formik.values.project ?? null}
                        onChange={(event, value) => {
                          formik.setFieldValue("project", value);
                          formik.setFieldTouched("project", true);

                          // keep your existing logic
                          onProjectNumberChange(event, value);
                        }}
                        loading={isOriginProjectsLoading}
                        key={isOriginProjectsLoading ? "project" : "project-loaded"}
                        filterOptions={getFilteredOptions}
                        renderOption={renderOptions}
                        getOptionLabel={getOptionLabel}
                        className={styles.projectIdField}
                        renderInput={(params) => {
                          const error = formik.errors.project;
                          const touched = formik.touched.project;

                          return (
                            <MInput
                              {...params}
                              name="project"
                              variant="outlined"
                              placeholder="Type or select a dataset ID"
                              error={Boolean(error && touched)}
                              helperText={touched ? error : ""}
                            />
                          );
                        }}
                      />
                  </div>
                  <div className={styles.headerlabel}>Select project</div>
                   <div className={styles.formItem}>                   
                      <div className={styles.label}>Workday ID*</div>
                      <MAutocomplete<WorkdayProjectOption>
                          options={(projects ?? [])
                            .filter(
                              (project): project is WorkdayProjectsDto =>
                                project.workdayId !== null && project.workdayId !== undefined
                            )
                            .map((project) => ({
                              ...project,
                              label: `${project.workdayId ?? ""} - ${project.name}`,
                            }))
                          }
                        
                          getOptionLabel={(option) => option.label}
                        
                          value={
                            projects
                              ?.filter((p): p is WorkdayProjectsDto => !!p.workdayId)
                              .map((project) => ({
                                ...project,
                                label: `${project.workdayId ?? ""} - ${project.name}`,
                              }))
                              .find(
                                (option) =>
                                  option.workdayId === formik.values.workdayProject?.workdayId
                              ) ?? null
                          }

                          loading={loading === LoadingState.pending}
                          onInputChange={handleInputChange}
                          onChange={(
                            _event: React.SyntheticEvent,
                            value: WorkdayProjectOption | null,
                            reason: string
                          ) => {
                            if (reason === "clear" || !value) {
                              setSelectedProjectName(null);
                        
                              formik.setFieldValue("workdayProject", {
                                name: "",
                                workdayId: "",
                                client: "",
                                costCenter: "",
                                workdayProjectType: undefined,
                              });
                        
                              return;
                            }
                        
                            // Fully typed, no cast needed
                            formik.setFieldValue("workdayProject", {
                              name: value.name,
                              workdayId: value.workdayId ?? "",
                              client: value.clientName,
                              costCenter: value.salesCode ?? "",
                              workdayProjectType: undefined, // Your DTO does not include this
                            });
                        
                            setSelectedProjectName(value.name);
                          }}
                          className={`${styles.projectIdField} ${styles.input}`}   // ensure height & padding apply
                          renderInput={(params) => {
                            const error = getIn(formik.errors, "workdayProject.workdayId") as string | undefined;
                            const touched = getIn(formik.touched, "workdayProject.workdayId") as boolean | undefined;
                        
                            return (
                              <MTextField
                                {...params}
                                name="workdayProject.workdayId"
                                variant="outlined"
                                size="small"
                                placeholder="Search or enter a Workday ID"
                                error={Boolean(error && touched)}
                                helperText={touched ? error : ""}
                              />
                            );
                          }}
                        />
                    </div>
                    <div className={styles.formItem}>
                      <div className={styles.label}>Workday Project Name *</div>  
                                          
                        <MTextField
                          name="workdayProject.name"
                          className={styles.input}
                          onChange={formik.handleChange}
                          value={formik.values.workdayProject?.name ?? ""}

                          error={Boolean(
                            getIn(formik.errors, "workdayProject.name") &&
                            getIn(formik.touched, "workdayProject.name")
                          )}
                          
                          helperText={
                            getIn(formik.touched, "workdayProject.name") as boolean
                              ? getIn(formik.errors, "workdayProject.name") as string
                              : ""
                          }

                          fullWidth
                          size="small"
                          variant="outlined"
                          placeholder="Enter the Workday project name"
                        />
                    </div>
                    <div className={styles.dynamiclabel}>
                      <div className={`${styles.labelWrapper} ${styles.halfWidth}`}>
                        <div className={styles.label}>Client</div>
                        <span className={styles.labelvalue}>
                          {formik.values.workdayProject?.client || '-'}
                        </span>
                      </div>
                      <div className={styles.labelWrapper}>
                        <div className={styles.label}>Cost Center</div>
                        <span className={styles.labelvalue}>
                          {formik.values.workdayProject?.costCenter || '-'}
                        </span>
                      </div>
                    </div>
                    <div className={styles.formItem}>
                      <div className={styles.label}>Project Type *</div>
                       <MAutocomplete
                          options={projectTypes}
                          getOptionLabel={(option) => option.label}

                          value={
                            projectTypes.find(
                              option =>
                                option.label === formik.values.workdayProject?.workdayProjectType
                            ) || null
                          }

                          onChange={(event, newValue) => {
                            formik.setFieldValue(
                              "workdayProject.workdayProjectType",
                              newValue ? newValue.label : ""
                            );
                          }}

                          renderInput={(params) => {
                            const error = getIn(
                              formik.errors,
                              "workdayProject.workdayProjectType"
                            ) as string | undefined;
                            const touched = getIn(
                              formik.touched,
                              "workdayProject.workdayProjectType"
                            ) as boolean | undefined;

                            return (
                              <MTextField
                                {...params}
                                name="workdayProject.workdayProjectType"
                                variant="outlined"
                                size="small"
                                placeholder="Select project type"
                                error={Boolean(error && touched)}
                                helperText={touched ? error : ""}
                              />
                            );
                          }}
                        />
                    </div>
                </div>
              )}
            </>
          )}
        </MForm>
      </>
    ),
    [originProjects, createProjectError, existingProject, handleSubmit, isOriginProjectsLoading, onProjectNumberChange, selectedProjectName],
  );

  const actions = useMemo(() => {
    if (existingProject) {
      return (
        <div className={styles.actions}>
          <MButton variant="outlined" color="primary" type="submit" size="small" form="project-form">
            Open Existing Dataset
          </MButton>
        </div>
      );
    }

    if (selectedSource) {
      return (
        <div className={styles.actions}>
          <MButton color="primary" type="button" variant="outlined" size="small" onClick={backToSources}>
            Cancel
          </MButton>
          <MButton
            disabled={isOriginProjectsLoading || isLoadingDictionaryData || !isAddEnabled}
            color="primary"
            type="submit"
            variant="outlined"
            size="small"
            form="project-form">
            Add and Open dataset
          </MButton>
        </div>
      );
    }
    return undefined;
  }, [selectedSource, isOriginProjectsLoading, isLoadingDictionaryData, styles, existingProject, isAddEnabled, backToSources]);

  const title = useMemo(() => {
    if (!selectedSource) {
      return "Select your survey data source";
    }
  }, [selectedSource]);

  const openSourceFromDatabase = useCallback(() => {
    dispatch(setAddCompositeProjectDialogOpenedState(false));
    dispatch(setAddProjectDialogOpenedState(true));
  }, [dispatch]);

  const openCreateCompositeProject = useCallback(() => {
    dispatch(setAddCompositeProjectDialogOpenedState(true));
    dispatch(setOpenCreateCompositeProject(true));
  }, [dispatch]);

  const openCreateCustomDataProject = useCallback(() => {
    dispatch(setAddProjectDialogOpenedState(false));
    dispatch(setOpenCreateCustomDataProject(true));
  }, [dispatch]);

  const openCreateWalrDataProject = useCallback(() => {
    dispatch(setAddProjectDialogOpenedState(false));
    dispatch(setOpenCreateWalrDataProject(true));
  }, [dispatch]);

  const handleBackClick = () => {
    dispatch(setAddProjectDialogOpenedState(false));
    if (currentUser?.isAdmin) 
      dispatch(setAddCompositeProjectDialogOpenedState(true));
    else 
      dispatch(setAddCompositeProjectDialogOpenedState(false));
  };

  const onOpen = useCallback(() => {
    if (currentUser?.isAdmin) {
      dispatch(setAddCompositeProjectDialogOpenedState(true));
    } else {
      dispatch(setAddProjectDialogOpenedState(true));
    }
  }, [dispatch, currentUser]);

  return (
    <>
      <MButton variant="outlined" color="primary" className={styles.addDatasetBtn} onClick={onOpen}>
        Add Dataset
      </MButton>
       <MDialog
        className={styles.sourceSelectionDialog}
        open={Boolean(isAddProjectDialogOpened)}
        onClose={onCloseDialog}
        fullWidth
        actionsElement={actions}
        titleElement={
            <div className={styles.box}>
              {(currentUser?.isAdmin) &&
                <MButton className={styles.btnBack} onClick={handleBackClick}>
                    <MIcons.ArrowBack className={styles.backIcon} />
                     Go back
                </MButton>
              }
              <div className={styles.title}>{title}</div>
            </div>
        }>
      <div className={styles.root}>{selectedSource ? projectSelect : sourceSelect}</div>
      </MDialog>

      <MDialog
        className={styles.dialog}
        open={Boolean(isAddCompositeProjectDialogOpened)}
        onClose={onCloseCompositeDialog}
        titleElement={<h4 className={styles.dialogTitle}>Select how you want to add a dataset</h4>}
        maxWidth={"md"}
        fullWidth>
        <div className={styles.content}>
          <div className={styles.item} onClick={() => openSourceFromDatabase()}>
            <div className={styles.info}>
               
                <MIcons.DatasourceIcon className={styles.dataIcon} />
                <div className={styles.infoSpan}><span>Add from Data Source</span></div>
              
            </div>
          </div>
          <div className={styles.item} onClick={() => openCreateCompositeProject()}>
            <div className={styles.info}>
                <MIcons.CompositeDatasetIcon className={styles.dataIcon} />
                <div><span>Create a composite dataset</span></div>
              </div>
            <div className={styles.description}>A composite dataset is a merged dataset</div>
            <div><BetaChip /></div>
          </div>
        </div>
      </MDialog>
      <CreateCompositeProjectDialog open={isOpenCreateCompositeProject} isWorkdayDataset= {false}/>
      <CreateCustomDataProjectDialog open={isOpenCreateCustomDataProject} isWorkdayDataset= {false} />
      <CreateWalrDataProjectDialog open={isOpenCreateWalrDataProject} isWorkdayDataset= {false} /> 
    </>
  );
});

export default AddProjectDialog;
